// SnackbarWrapper.tsx
import { SyntheticEvent, FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Snackbar, Alert } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { hideSnackbar } from "../../redux/actions";

const iconMap: Record<string, React.ReactElement> = {
  success: <CheckCircleOutlineIcon sx={{ fontSize: 22 }} />,
  error: <ErrorOutlineIcon sx={{ fontSize: 22 }} />,
  info: <InfoOutlinedIcon sx={{ fontSize: 22 }} />,
  warning: <WarningAmberIcon sx={{ fontSize: 22 }} />,
};

const severityStyles: Record<string, { bg: string; border: string; color: string }> = {
  success: { bg: "rgba(255, 255, 255, 0.95)", border: "1px solid #e2e8f0", color: "#1e293b" },
  error:   { bg: "rgba(255, 255, 255, 0.95)", border: "1px solid rgba(239, 68, 68, 0.3)", color: "#991b1b" },
  warning: { bg: "rgba(255, 255, 255, 0.95)", border: "1px solid rgba(245, 158, 11, 0.3)", color: "#92400e" },
  info:    { bg: "rgba(255, 255, 255, 0.95)", border: "1px solid rgba(59, 130, 246, 0.3)", color: "#1e40af" },
};

const SnackbarWrapper: FC = () => {
  const dispatch = useDispatch();
  const snackbar = useSelector((state: any) => state.snackbar);

  const handleClose = (event: SyntheticEvent<Element, Event> | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    dispatch(hideSnackbar());
  };

  const styles = severityStyles[snackbar?.severity] || severityStyles.info;

  return (
    <Snackbar
      open={snackbar?.open}
      autoHideDuration={snackbar?.severity === "success" ? 4000 : 6000}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        onClose={handleClose}
        severity={snackbar?.severity}
        icon={iconMap[snackbar?.severity] || undefined}
        variant="standard"
        sx={{
          width: "100%",
          minWidth: 360,
          borderRadius: "16px",
          fontWeight: 600,
          fontSize: "0.9rem",
          bgcolor: styles.bg,
          color: styles.color,
          border: styles.border,
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
          py: 1.2,
          px: 2.5,
          "& .MuiAlert-icon": { mr: 1.5, alignItems: "center", color: styles.color },
          "& .MuiAlert-action": { pt: 0 },
        }}
      >
        {snackbar?.message}
      </Alert>
    </Snackbar>
  );
};

export default SnackbarWrapper;
